import {
    TOPIC_NOTIFY_CHANGED_RAW,
    TOPIC_LEADER_CONNECTION_STATUS,
    TOPIC_FORCE_RECONNECT,
    TOPIC_CHAT_MESSAGE,
    type ConnectionStatus,
} from 'lib/ws/constants'
import type { Service } from 'lib/ws/election'
import { getWsHub } from 'lib/ws/hub'
import {
    AuthMessage,
    ChangedRef,
    ChatMessage,
    ClientMessage,
    ServerMessage,
} from 'pbts/c1/api/websocketnotify/v1/websocketnotify'
import { getChatService } from './chat-service'
import { websocketFilterManager } from './websocket-filter-manager'

// Types replicated from existing notify client to keep consistent payloads
export type NotifyChangedEvent = ArrayBuffer

// Retry configuration
const BASE_RETRY_DELAY = 3000
const MAX_RETRY_DELAY = 60000
const DEGRADED_RETRY_THRESHOLD = 3

/**
 * Leader-only service that owns the single /ws/notify socket and broadcasts
 * raw ChangedRef buffers to followers via the hub.
 */
export class NotifyService implements Service {
    public readonly namespace = 'notify'
    private socketInstance: WebSocket | undefined
    private retryCount = 0
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null
    private readonly getAccessToken: () => Promise<string | undefined>
    // Connection attempt counter to prevent stale handlers from operating on wrong socket
    private connectionAttempt = 0

    constructor(getAccessToken: () => Promise<string | undefined>) {
        this.getAccessToken = getAccessToken

        // Initialize the filter manager
        websocketFilterManager.initialize(getAccessToken)
    }

    public async init(_hub?: import('lib/ws/election').Hub): Promise<void> {
        this.setupVisibilityHandler()
        this.setupForceReconnectHandler()
        // Connection is established via onBecomeLeader(), not here
        // This prevents double-connect when registering on an already-leader tab
    }

    /**
     * Called when this tab becomes the leader.
     */
    public async onBecomeLeader(): Promise<void> {
        // Broadcast 'reconnecting' so followers know to re-announce their subscriptions
        // when the new leader connects. Without this, followers might not see a state
        // transition (old leader was 'connected', new leader is also 'connected').
        this.broadcastStatus('reconnecting')
        await this.connect()
    }

    private setupForceReconnectHandler(): void {
        const hub = getWsHub()
        hub?.subscribe(TOPIC_FORCE_RECONNECT, () => {
            // Only leader handles this - reset retry count and reconnect immediately
            if (!getWsHub()?.isLeader()) return
            this.retryCount = 0
            void this.connect()
        })
    }

    private setupVisibilityHandler(): void {
        if (typeof document === 'undefined') return

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return

            // Only leader should manage WebSocket connection
            if (!getWsHub()?.isLeader()) return

            // If tab becomes visible and socket is not open, try to reconnect immediately
            const isSocketDead = !this.socketInstance || this.socketInstance.readyState !== WebSocket.OPEN
            if (isSocketDead) {
                this.retryCount = 0
                void this.connect()
            }
        })
    }

    // eslint-disable-next-line class-methods-use-this
    private broadcastStatus(status: ConnectionStatus): void {
        const hub = getWsHub()
        hub?.publish(TOPIC_LEADER_CONNECTION_STATUS, { status })
    }

    private scheduleReconnect(): void {
        // Clear any existing pending reconnect
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        const retryTime = Math.min(
            BASE_RETRY_DELAY * 2 ** this.retryCount + Math.floor(Math.random() * BASE_RETRY_DELAY),
            MAX_RETRY_DELAY,
        )

        this.retryCount++

        // Broadcast degraded status after threshold
        if (this.retryCount > DEGRADED_RETRY_THRESHOLD) {
            this.broadcastStatus('degraded')
        } else {
            this.broadcastStatus('reconnecting')
        }

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            void this.connect()
        }, retryTime)
    }

    // No RPC methods are needed for now; followers derive filtering client-side.
    private async connect(): Promise<void> {
        // Increment connection attempt to invalidate any pending handlers from previous attempts
        const currentAttempt = ++this.connectionAttempt

        // Close any existing connection first to prevent duplicates
        if (this.socketInstance) {
            const oldSocket = this.socketInstance
            this.socketInstance = undefined
            try {
                oldSocket.close(1000, 'Reconnecting')
            } catch {
                // Ignore close errors
            }
        }

        let token: string | undefined
        try {
            token = await this.getAccessToken()
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[NotifyService] Failed to get access token, scheduling retry', err)
            this.scheduleReconnect()
            return
        }

        if (!token) {
            // eslint-disable-next-line no-console
            console.warn('[NotifyService] No access token available, scheduling retry')
            this.scheduleReconnect()
            return
        }

        // Clean up any existing send function before creating new socket
        websocketFilterManager.onDisconnect()
        getChatService()?.onDisconnect()

        this.socketInstance = new WebSocket(`wss://${location.host}/ws/notify`)

        this.socketInstance.onopen = (): void => {
            // Ignore if this is a stale connection attempt
            if (currentAttempt !== this.connectionAttempt) return
            // Clear any pending reconnect timer
            if (this.reconnectTimer !== null) {
                clearTimeout(this.reconnectTimer)
                this.reconnectTimer = null
            }

            // Create send function for services
            const sendFn = (data: Uint8Array): void => {
                if (this.socketInstance?.readyState !== WebSocket.OPEN) {
                    websocketFilterManager.onDisconnect()
                    getChatService()?.onDisconnect()
                    return
                }
                this.socketInstance.send(data)
            }

            // Register send function with filter manager
            websocketFilterManager.registerSendFunction(sendFn)

            // Register send function with chat service
            getChatService()?.registerSendFunction(sendFn)

            // Send initial auth message
            const authMessage = AuthMessage.create({
                accessToken: token,
            })

            const clientMessage = ClientMessage.create({
                message: {
                    oneofKind: 'auth',
                    auth: authMessage,
                },
            })

            const binaryData = ClientMessage.toBinary(clientMessage)
            this.socketInstance?.send(binaryData)

            this.retryCount = 0
            this.broadcastStatus('connected')

            // Notify chat service of connection recovery
            void getChatService()?.onConnectionRecovered()
        }

        this.socketInstance.onclose = (event): void => {
            // Ignore if this is a stale connection attempt
            if (currentAttempt !== this.connectionAttempt) return

            this.socketInstance = undefined

            // Clean up the registered send functions to avoid stale closures
            websocketFilterManager.onDisconnect()
            getChatService()?.onDisconnect()

            if (event.code !== 1000) {
                // eslint-disable-next-line no-console
                console.error('[NotifyService] WebSocket closed unexpectedly, scheduling retry', {
                    code: event.code,
                    reason: event.reason,
                })
                this.scheduleReconnect()
            }
        }

        this.socketInstance.onerror = (event): void => {
            // Ignore if this is a stale connection attempt
            if (currentAttempt !== this.connectionAttempt) return

            // eslint-disable-next-line no-console
            console.error('[NotifyService] WebSocket error, scheduling retry', event)
            // Clean up the registered send functions to avoid stale closures
            websocketFilterManager.onDisconnect()
            getChatService()?.onDisconnect()
        }

        this.socketInstance.onmessage = async (event): Promise<void> => {
            // Ignore if this is a stale connection attempt
            if (currentAttempt !== this.connectionAttempt) return
            try {
                const d = await event.data.arrayBuffer()
                const bytes = new Uint8Array(d)

                // Try to parse as ServerMessage first (new format)
                try {
                    const serverMsg = ServerMessage.fromBinary(bytes)
                    // Only route if oneofKind is set, otherwise fall back to legacy parsing
                    if (serverMsg.message.oneofKind) {
                        this.routeServerMessage(serverMsg, d)
                        return
                    }
                } catch {
                    // Fall through to legacy ChangedRef parsing
                }

                // Legacy: Validate the payload is a ChangedRef; if it fails, ignore
                try {
                    ChangedRef.fromBinary(bytes)
                    const hub = getWsHub()
                    hub?.publish(TOPIC_NOTIFY_CHANGED_RAW, d as NotifyChangedEvent)
                } catch {
                    // Ignore unrecognized messages
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[NotifyService] error reading message', err)
            }
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private routeServerMessage(msg: ServerMessage, _rawData: ArrayBuffer): void {
        const hub = getWsHub()
        if (!hub) return

        switch (msg.message.oneofKind) {
            case 'changedRef': {
                // Serialize just the ChangedRef, not the full ServerMessage wrapper
                const changedRefBytes = ChangedRef.toBinary(msg.message.changedRef)
                // Slice buffer to handle potential byteOffset issues
                const buffer = changedRefBytes.buffer.slice(
                    changedRefBytes.byteOffset,
                    changedRefBytes.byteOffset + changedRefBytes.byteLength,
                )
                hub.publish(TOPIC_NOTIFY_CHANGED_RAW, buffer as NotifyChangedEvent)
                break
            }
            case 'chatMessage': {
                // Serialize to binary for cross-tab BroadcastChannel compatibility
                // (protobuf objects lose their prototype during structured cloning)
                const chatBytes = ChatMessage.toBinary(msg.message.chatMessage)
                const chatBuffer = chatBytes.buffer.slice(
                    chatBytes.byteOffset,
                    chatBytes.byteOffset + chatBytes.byteLength,
                )
                hub.publish(TOPIC_CHAT_MESSAGE, chatBuffer)
                break
            }
            case 'authAck':
                // Connection ready
                break
            case 'error':
                // eslint-disable-next-line no-console
                console.error('[NotifyService] Server error:', msg.message.error)
                break
            default:
                break
        }
    }
}
